const CONTAINER_SELECTOR = '[ref="acceleratedCheckoutButtonContainer"]';
const BLOCKED_CLASS = 'accelerated-checkout-block--upload-blocked';
const FEE_BLOCKED_CLASS = 'accelerated-checkout-block--fee-blocked';

/**
 * Shopify only stores file line item properties when the browser posts the product
 * form as multipart/form-data to /cart/add. Accelerated checkout builds its own
 * checkout from the variant and the plain form values, so a file input contributes
 * nothing but the browser's placeholder path and the order arrives without artwork.
 *
 * Only fields the customer can actually submit count. Upload blocks that are hidden
 * by conditional display, a design selection, or a product tag rule disable their
 * inputs (or never render at all), so those products keep accelerated checkout.
 *
 * @param {HTMLInputElement} input
 * @returns {boolean}
 */
const isInUse = (input) => !input.matches(':disabled') && input.closest('[hidden]') == null;

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
const collectsFiles = (form) =>
  Array.from(form.elements).some(
    (element) => element instanceof HTMLInputElement && element.type === 'file' && isInUse(element)
  );

const normalizeOption = (value) => String(value).replace(/\u00a0|&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

const requiresFees = (form) => {
  const controls = Array.from(form.elements).filter((control) =>
    !control.matches(':disabled') && !control.parentElement?.closest('[hidden]')
  );
  const blocks = new Set(form.querySelectorAll('[data-fee-config]'));
  if (form.id) {
    document.querySelectorAll(`[data-fee-config][data-form="${CSS.escape(form.id)}"]`).forEach((block) => blocks.add(block));
  }
  const quantity = parseInt(controls.find((control) => control.name === 'quantity')?.value, 10) || 1;
  const hasFee = (config) => {
    if (typeof config === 'string') return Boolean(config.trim());
    if (!config || !(config.handle || config.productHandle || config.variantId || config.variant_id)) return false;
    const threshold = parseInt(config.minQtyWaive, 10);
    return !config.minQtyWaive || Number.isNaN(threshold) || quantity < threshold;
  };

  return Array.from(blocks).some((block) => {
    if (block.closest('[hidden]')) return false;
    let config;
    try {
      config = JSON.parse(block.querySelector('script[data-fee-config-json]')?.textContent?.trim() || block.dataset.feeConfig);
    } catch {
      return false;
    }
    if (!config?.enabled) return false;
    const key = block.dataset.key || block.dataset.feePropertyKey;
    const propertyControls = controls.filter((control) => control.name === `properties[${key}]`);
    const selected = propertyControls.filter((control) =>
      !['checkbox', 'radio'].includes(control.type) || control.checked
    );
    const values = selected.map((control) => control.value?.trim()).filter(Boolean);

    switch (block.dataset.feeType) {
      case 'patch-guided-addon':
        return values.length > 0;
      case 'checkbox':
      case 'text':
        return values.length > 0 && hasFee(config);
      case 'personalization':
        return hasFee(config) && controls.some((control) =>
          control.name === `properties[${block.dataset.personalizationTextKey}]` && control.value?.trim()
        );
      case 'select':
      case 'radio':
      case 'multiselect':
      case 'swatch-checkbox': {
        const selectedInputs = Array.from(block.querySelectorAll('input[data-fee-handle]:checked')).filter(isInUse);
        if (selectedInputs.some((input) => hasFee({ handle: input.dataset.feeHandle, minQtyWaive: input.dataset.feeMinQtyWaive }))) return true;
        const options = ['multiselect', 'swatch-checkbox'].includes(block.dataset.feeType)
          ? values.flatMap((value) => value.split(',')) : values;
        return options.some((value) => {
          const normalized = normalizeOption(value);
          const slug = normalized.replace(/\s+/g, '_');
          const fee = Object.entries(config.optionFees || {}).find(([option]) =>
            normalizeOption(option) === normalized || normalizeOption(option).replace(/\s+/g, '_') === slug
          );
          return fee && hasFee(fee[1]);
        });
      }
      default:
        return false;
    }
  });
};

/** @param {Element} container */
const syncContainer = (container) => {
  const form = container.closest('form');
  if (!(form instanceof HTMLFormElement)) return;

  container.classList.toggle(BLOCKED_CLASS, collectsFiles(form));
  container.classList.toggle(FEE_BLOCKED_CLASS, requiresFees(form));
};

/** @param {ParentNode} [root] */
const syncAll = (root) => {
  for (const container of (root ?? document).querySelectorAll(CONTAINER_SELECTOR)) {
    syncContainer(container);
  }
};

let pendingSync = false;

/**
 * Conditional option blocks enable and disable their inputs while handling the
 * change event, so wait a frame before reading the form back.
 */
const scheduleSync = () => {
  if (pendingSync) return;

  pendingSync = true;
  requestAnimationFrame(() => {
    pendingSync = false;
    syncAll();
  });
};

syncAll();

document.addEventListener('change', scheduleSync, { capture: true });
document.addEventListener('input', scheduleSync, { capture: true });
document.addEventListener('reset', scheduleSync, { capture: true });
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  syncAll(target instanceof Element ? target : undefined);
});

// Upload blocks are also shown and hidden outside of form events, for instance when a
// pre-made design is picked or when another script moves blocks into the form.
new MutationObserver(scheduleSync).observe(document.documentElement, {
  subtree: true,
  childList: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['disabled', 'hidden', 'value', 'checked', 'selected', 'data-fee-config', 'data-form', 'form', 'data-fee-handle', 'data-fee-min-qty-waive'],
});
